import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import _ from 'lodash';

import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import type { UseQueryResult } from '@tanstack/react-query';
import type { FieldValue, FormElementSchema, FormSchema } from '../types';
import { Element } from './layout/Element';

import {
  pickAnsweredFields,
  getDefaultFormValues,
  isObjectShapeOf,
} from '../parsing/parser';

import { useAPIErrorHandler } from '../../components/hooks/useAPIErrorHandler';

import { FormMetadataContext } from '../context/FormMetaDataContext';

import type { Nullable } from '../../types/common/utility';

import { DefaultActions } from './actions/DefaultActions';
import type { ActionsComponent } from './actions/types';
import { useFormValidationSchema } from '../parsing/validations';
import type { ProductType } from '../../types';

import {
  updateFormFieldsForProduct,
  watchFieldsBasedOnProductType,
} from '../utils/form.utils';

export type FormProps = {
  accountId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: Nullable<Record<string, any>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmitSuccess?: (resp?: any) => void;
  Actions?: ActionsComponent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValues?: Record<string, any>;
  useDefaultValuesFromFormSchema?: boolean;
  productType?: ProductType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit?: (payload: Record<string, FieldValue>) => Promise<any>;
  elementLabelSxProps?: SxProps<Theme>;
  disabled?: boolean;
  formId?: string;
  showConfidence?: boolean;
  /**
   * Preserve in-progress field values, errors, and touched state when defaultValues
   * change. Dirty flags are recomputed against the new defaults so Save/Cancel
   * disable once current values already match the refreshed defaults.
   * @default false
   */
  keepStateOnReset?: boolean;
  /**
   * Values that take precedence over the schema's saved values when building
   * defaults.
   *
   * `getDefaultFormValues` resolves `savedValue ?? uiDefaultValue ?? ...`, so
   * `defaultValues` cannot override an answer the server already holds. The
   * Risk Data draft overlay needs exactly that: its store, not the last
   * response, is the authority on what a field currently reads as while
   * accepted answers are still unsaved.
   *
   * Absent everywhere else, so every other form keeps today's precedence.
   */
  valueOverrides?: Record<string, FieldValue>;
  /**
   * Enable Document RAG extraction for form fields
   * @default false
   */
  enableRag?: boolean;
  /**
   * Submission ID for RAG extraction API calls
   */
  submissionId?: string;
  /**
   * Risk Engineering account edits should not be blocked by ZSP submission required flags.
   */
  readonly relaxRequiredValidation?: boolean;
} & (
  | {
      form: FormSchema;
      formSchemaQuery?: never;
    }
  | { formSchemaQuery: UseQueryResult<FormSchema>; form?: never }
);

export const Form = ({
  useDefaultValuesFromFormSchema = true,
  productType,
  onSubmit,
  form,
  disabled = false,
  showConfidence = false,
  keepStateOnReset = false,
  enableRag = false,
  submissionId,
  ...props
}: FormProps) => {
  const handleAPIError = useAPIErrorHandler();
  const _form = form ?? props.formSchemaQuery?.data;

  const defaultValues = React.useMemo(() => {
    if (!_form) {
      return {};
    }
    return {
      ...getDefaultFormValues(
        _form,
        props.defaultValues,
        useDefaultValuesFromFormSchema
      ),
      ...(props.valueOverrides ?? {}),
    };
  }, [
    _form,
    props.defaultValues,
    props.valueOverrides,
    useDefaultValuesFromFormSchema,
  ]);

  const formValidationSchema = useFormValidationSchema({
    formSchema: _form,
    productType,
    relaxRequiredValidation: props.relaxRequiredValidation,
  });

  const formMethods = useForm({
    defaultValues,
    resolver: yupResolver(formValidationSchema),
  });

  const { reset: resetForm, watch, setValue } = formMethods;
  const watchFields = watchFieldsBasedOnProductType(productType, watch);

  React.useEffect(() => {
    // When true: keep in-progress values/errors/touched; recompute dirty vs new defaults.
    resetForm(
      defaultValues,
      keepStateOnReset
        ? {
            keepErrors: true,
            keepDirtyValues: true,
            keepTouched: true,
          }
        : undefined
    );
  }, [defaultValues, resetForm, keepStateOnReset]);

  React.useEffect(() => {
    updateFormFieldsForProduct(productType, watchFields, setValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchFields]);

  const haveDirtyFields =
    Object.keys(formMethods.formState.dirtyFields).length > 0;

  const handleSubmit = (formData: Record<string, FieldValue>) => {
    const payload = pickAnsweredFields(formData);
    const payloadEqualsFormValues = _.isEqual(payload, _form.values);

    if (payloadEqualsFormValues && !haveDirtyFields) {
      props.onSubmitSuccess?.();
      return;
    }

    return onSubmit?.(payload)
      .then((resp) => props.onSubmitSuccess?.(resp?.data))
      .catch(handleAPIError('Failed to save answers.'));
  };

  // Defers rendering until RHF form state has been updated with a new template's fields
  if (!isObjectShapeOf(defaultValues, formMethods.getValues())) {
    return null;
  }

  if (!_form) {
    return null;
  }

  return (
    <React.Fragment key={_form?.id}>
      <FormProvider {...formMethods}>
        <form
          id={_form?.id}
          onSubmit={formMethods.handleSubmit(handleSubmit)}
          noValidate
        >
          <FormMetadataContext.Provider value={props.data ?? null}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap="24px"
            >
              {_form.elements.map((element: FormElementSchema) => {
                return (
                  <Element
                    fields={_form.fields}
                    values={_form.values}
                    formId={_form.id}
                    productType={productType}
                    disabled={disabled}
                    elementLabelSxProps={props.elementLabelSxProps}
                    showConfidence={showConfidence}
                    enableRag={enableRag}
                    submissionId={submissionId}
                    {...element}
                  />
                );
              })}
            </Box>

            {typeof props.Actions == 'function' ? (
              <props.Actions formMethods={formMethods} />
            ) : (
              <DefaultActions formMethods={formMethods} />
            )}
          </FormMetadataContext.Provider>
        </form>
      </FormProvider>
    </React.Fragment>
  );
};
